import type { Program } from 'models';
import { useCallback, useMemo } from 'react';
import { usePermissions } from 'permissions';
import { Flag1LineIcon, MinusCircleIcon, UserSecurityLineIcon } from 'ui';
import { routes as adminDashboardRoutes, useAdminDashboardRedirectMutation } from 'features/admin-dashboard';
import { useConfirmLeaveProgram } from './useConfirmLeaveProgram';
import { useConfirmReportProgram } from './useConfirmReportProgram';

type P = {
  onLeaveSuccess: () => Promise<void>;
};

export const useProgramMenuActions = ({ onLeaveSuccess }: P) => {
  const { canReportContentObject, canUnsubscribe, canViewAdminDashboardPrograms } = usePermissions();
  const { isPending: isRedirectingToAdminDashboard, mutate: redirectToAdminDashboard } =
    useAdminDashboardRedirectMutation();
  const { confirmLeaveProgram, contextHolder: leaveProgramContextHolder } = useConfirmLeaveProgram({
    onLeaveSuccess,
  });
  const { confirmReportProgram, contextHolder: reportProgramContextHolder } = useConfirmReportProgram();

  const viewInAdminConsole = useCallback(
    (programToView: Program) => {
      redirectToAdminDashboard({ referrer: adminDashboardRoutes.program.detail(programToView.id) });
    },
    [redirectToAdminDashboard],
  );

  const getMenuItems = useCallback(
    (program: Program) => {
      const items = [];

      if (canViewAdminDashboardPrograms({ organizationId: program.organization?.id, programId: program.id })) {
        items.push({
          disabled: isRedirectingToAdminDashboard,
          icon: <UserSecurityLineIcon />,
          key: 'view-in-admin-console',
          label: 'View in admin console',
        });
      }

      if (canReportContentObject({ contentObject: program })) {
        items.push({
          icon: <Flag1LineIcon />,
          key: 'report',
          label: 'Report',
        });
      }

      if (canUnsubscribe({ program })) {
        items.push({
          danger: true,
          icon: <MinusCircleIcon />,
          key: 'leave',
          label: 'Unsubscribe',
        });
      }

      return items;
    },
    [canReportContentObject, canUnsubscribe, canViewAdminDashboardPrograms, isRedirectingToAdminDashboard],
  );

  const handleMenuAction = useCallback(
    (key: string, program: Program) => {
      switch (key) {
        case 'leave':
          confirmLeaveProgram(program);
          break;
        case 'report':
          confirmReportProgram(program);
          break;
        case 'view-in-admin-console':
          viewInAdminConsole(program);
          break;
      }
    },
    [confirmLeaveProgram, confirmReportProgram, viewInAdminConsole],
  );

  return useMemo(
    () => ({ getMenuItems, handleMenuAction, leaveProgramContextHolder, reportProgramContextHolder }),
    [getMenuItems, handleMenuAction, leaveProgramContextHolder, reportProgramContextHolder],
  );
};
